// Product Upsell Drawer - Prestige Theme Version
// Adapted for Prestige Theme's Web Component architecture
// Features: Shows subscription interval, dynamic pricing, variant selection, cart filtering
// Supports: ReCharge + ABlyft subscription systems

(function() {
  'use strict';

  // ============================================
  // SINGLE INSTANCE GUARD
  // ============================================
  // Ensure this script only initializes once, even if loaded multiple times.
  // The drawer lives on document.body and is reused — never recreated.
  if (window._upsellDrawerInitialized) return;
  window._upsellDrawerInitialized = true;

  const upsellDrawer = document.querySelector('#product-notification-drawer');
  let currentVariantId = null;
  let currentLineKey = null;
  let currentQuantity = 1;
  let isUpdating = false;
  let currentCart = null;

  if (!upsellDrawer) return;

  // Theme-Option: Variant-Selector in den Upsell-Karten anzeigen (Default: an)
  const showVariantSelector = upsellDrawer.dataset.showVariantSelector !== 'false';

  // ============================================
  // FIX 2: BLOCK PRESTIGE CART DRAWER
  // ============================================
  // Prestige opens cart-drawer via a DIRECT call chain:
  //   ProductForm.onSubmit_fn → CartDrawer.onCartChange_fn → cart-drawer.show()
  // This bypasses the event system entirely, so stopImmediatePropagation on
  // variant:add does NOT prevent it. We must monkey-patch show() itself.
  const cartDrawerEl = document.querySelector('cart-drawer');
  let _blockCartDrawer = false;

  if (cartDrawerEl && typeof cartDrawerEl.show === 'function') {
    const _origShow = cartDrawerEl.show.bind(cartDrawerEl);
    cartDrawerEl.show = function(...args) {
      if (_blockCartDrawer) {
        console.log('[Upsell Drawer] Blocked cart-drawer.show()');
        _blockCartDrawer = false;
        return Promise.resolve();
      }
      return _origShow(...args);
    };
    console.log('[Upsell Drawer] Monkey-patched cart-drawer.show()');
  }

  // Also intercept fetch to /cart/add to set the block flag at the EARLIEST
  // possible moment - before Prestige's onSubmit callback chain even runs.
  const _origFetch = window.fetch;
  window.fetch = function(url, options) {
    const result = _origFetch.apply(this, arguments);
    
    if (typeof url === 'string' && url.includes('/cart/add') && 
        window._pendingUpsellProducts && window._pendingUpsellProducts.length > 0) {
      result.then(() => {
        _blockCartDrawer = true;
        console.log('[Upsell Drawer] Set cart drawer block flag (from /cart/add response)');
      });
    }
    
    return result;
  };

  // ============================================
  // THEME DETECTION & COMPATIBILITY LAYER
  // ============================================
  
  const themeConfig = {
    // Cart drawer element selector
    cartDrawerSelector: '#cart-drawer',
    // Cart items container selector
    cartItemsSelector: '.cart-drawer__items',
    // Cart count element selectors (in order of preference)
    cartCountSelectors: [
      'cart-count',           // Prestige uses <cart-count> web component
      '.cart-count',
      '[data-cart-count]',
      '.Header__CartCount',   // Old theme fallback
      '.CartCount',
      '#cart-count'
    ],
    // Section ID for cart drawer (used for section rendering)
    cartDrawerSectionId: null, // Will be detected
    // How to open cart drawer
    openCartDrawerMethod: 'prestige' // 'prestige' | 'legacy'
  };

  // Detect theme and configure accordingly
  function detectTheme() {
    const prestigeCartDrawer = document.querySelector('cart-drawer');
    const legacyCartDrawer = document.querySelector('#sidebar-cart');
    
    if (prestigeCartDrawer) {
      themeConfig.cartDrawerSelector = 'cart-drawer';
      themeConfig.cartItemsSelector = '.cart-drawer__items';
      themeConfig.openCartDrawerMethod = 'prestige';
      
      // Get section ID from parent shopify-section
      const shopifySection = prestigeCartDrawer.closest('.shopify-section');
      if (shopifySection) {
        themeConfig.cartDrawerSectionId = shopifySection.id.replace('shopify-section-', '');
      }
      
      console.log('[Upsell Drawer] Detected Prestige theme');
    } else if (legacyCartDrawer) {
      themeConfig.cartDrawerSelector = '#sidebar-cart';
      themeConfig.cartItemsSelector = '.Cart__ItemList';
      themeConfig.openCartDrawerMethod = 'legacy';
      console.log('[Upsell Drawer] Detected Legacy theme');
    }
  }
  
  detectTheme();

  // ============================================
  // CART REQUEST QUEUE - Prevents race conditions
  // ============================================
  
  const cartQueue = {
    queue: [],
    processing: false,
    
    add(fn) {
      return new Promise((resolve, reject) => {
        this.queue.push({ fn, resolve, reject });
        this.process();
      });
    },
    
    async process() {
      if (this.processing || this.queue.length === 0) return;
      
      this.processing = true;
      const { fn, resolve, reject } = this.queue.shift();
      
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        this.processing = false;
        // Small delay between requests to let Shopify settle
        setTimeout(() => this.process(), 100);
      }
    }
  };

  // Wrapper for queued cart fetch
  function queuedCartFetch(url, options = {}) {
    return cartQueue.add(() => fetch(url, options).then(r => {
      if (!r.ok) throw new Error(`Cart request failed: ${r.status}`);
      return r.json();
    }));
  }

  // ============================================
  // SUBSCRIPTION INTERVAL DETECTION
  // ============================================

  // NUR NOCH FALLBACK. Autoritativ ist die Frequenz aus dem Warenkorb
  // (selling_plan_allocation, siehe unten im /cart.js-Then). Diese Funktion
  // liest den DOM und kann daher eine Frequenz behaupten, die nie im
  // Warenkorb angekommen ist. Nicht wieder zur Hauptquelle machen -- docs/61.
  function getSubscriptionInterval() {
    // Check ReCharge widget
    const rechargeSelect = document.querySelector('.rc_selling_plans__dropdown select, select[name="selling_plan"]');
    if (rechargeSelect && rechargeSelect.value) {
      const selectedOption = rechargeSelect.options[rechargeSelect.selectedIndex];
      const intervalText = selectedOption.textContent || selectedOption.innerText;
      
      const match = intervalText.match(/(\d+)\s*(Tag|Woche|Monat)/i);
      if (match) {
        const number = match[1];
        const unit = match[2].toLowerCase();
        
        const unitMap = {
          'tag': number == 1 ? 'Tag' : 'Tage',
          'tage': 'Tage',
          'woche': number == 1 ? 'Woche' : 'Wochen',
          'wochen': 'Wochen',
          'monat': number == 1 ? 'Monat' : 'Monate',
          'monate': 'Monate'
        };
        
        return `Lieferung alle ${number} ${unitMap[unit] || 'Tage'}`;
      }
    }

    // Check ABlyft frequency selector
    const ablyftFrequency = document.querySelector('#frequency');
    if (ablyftFrequency && ablyftFrequency.value) {
      const selectedOption = ablyftFrequency.options[ablyftFrequency.selectedIndex];
      const optionText = selectedOption.getAttribute('data-plan-option') || selectedOption.textContent;
      return optionText;
    }

    return null;
  }

  function isSubscriptionSelected() {
    // Check for ABlyft sections
    const ablyftSubscriptionSection = document.querySelector('#subscriptionSection.active');
    if (ablyftSubscriptionSection) return true;

    // Check for ReCharge
    const rechargeSubscription = document.querySelector('input[name="purchase_type"][value="autodeliver"]:checked');
    if (rechargeSubscription) return true;

    const rechargeWidget = document.querySelector('.rc_widget__option--active');
    if (rechargeWidget && rechargeWidget.textContent.toLowerCase().includes('abo')) return true;

    return false;
  }

  // ============================================
  // PRICE DETECTION
  // ============================================

  function getActualDisplayedPrice() {
    if (typeof window.getCurrentDisplayedPrice === 'function') {
      const priceData = window.getCurrentDisplayedPrice();
      if (priceData) {
        return priceData.price;
      }
    }

    // Check for ReCharge subscription price (even if hidden in DOM)
    const isSubscription = isSubscriptionSelected();
    if (isSubscription) {
      const rechargePrice = document.querySelector('.rc_widget__option--active .rc_widget__price');
      if (rechargePrice) {
        const priceText = rechargePrice.textContent.trim();
        const priceMatch = priceText.match(/[\d.,]+/);
        if (priceMatch) {
          const price = priceMatch[0].replace(',', '.');
          const priceInCents = Math.round(parseFloat(price) * 100);
          return priceInCents;
        }
      }
    }

    const priceSelectors = [
      '.ProductMeta__Price .Price--highlight',
      '.ProductMeta__PriceList .Price--highlight',
      '.product__price .price-item--sale',
      '.price__regular .price-item--regular',
      '.tl-0050-section.active .tl-0050-section-price-current',
      // Prestige theme selectors
      '.product-info .price',
      '.product-info sale-price'
    ];
    
    for (const selector of priceSelectors) {
      const priceEl = document.querySelector(selector);
      if (priceEl && priceEl.offsetParent !== null) {
        const priceText = priceEl.textContent.trim();
        const priceMatch = priceText.match(/[\d.,]+/);
        if (priceMatch) {
          const price = priceMatch[0].replace(',', '.');
          const priceInCents = Math.round(parseFloat(price) * 100);
          return priceInCents;
        }
      }
    }
    
    return null;
  }

  // ============================================
  // MAIN NOTIFICATION FUNCTION
  // ============================================

  window._showProductNotification = function(productDetail) {
    if (!upsellDrawer || !productDetail) return;

    const variant = productDetail.variant;
    const quantity = productDetail.quantity;
    let upsellProducts = productDetail.upsell_products || [];
    
    if (!variant) return;
    
    let variantId = variant.id || variant.variant_id;
    
    if (typeof variantId === 'string') {
      if (variantId.includes(':')) {
        variantId = variantId.split(':').pop();
      }
      variantId = variantId.replace(/\D/g, '');
    }
    variantId = parseInt(variantId, 10);
    
    currentVariantId = variantId;
    currentQuantity = quantity;

    const isSubscription = isSubscriptionSelected();
    const subscriptionInterval = isSubscription ? getSubscriptionInterval() : null;

    const originalCompareAtPrice = variant.compare_at_price;
    const displayedPrice = getActualDisplayedPrice();

    // Bei Abo NICHT den gescrapten DOM-Preis verwenden: variant.price ist hier bereits
    // der korrekte, selling-plan-adjustierte Cart-Preis (lineItem.price aus /cart.js).
    // getActualDisplayedPrice() liefert im Abo-Fall faelschlich den 1x-Abo-Preis
    // (Selektoren passen nicht zur custom Abo-ProductForm) -> Cart-Preis beibehalten.
    if (!isSubscription && displayedPrice !== null && displayedPrice !== variant.price) {
      if (!originalCompareAtPrice || originalCompareAtPrice <= variant.price) {
        variant.compare_at_price = variant.price;
      }
      variant.price = displayedPrice;
    }

    window._currentSubscriptionInterval = subscriptionInterval;

    // Warenkorb vom variant:add-Handler übernehmen statt ein drittes Mal /cart.js zu laden
    // (Beschleunigung 20.10., docs/69). Andere Aufrufer ohne Warenkorb laden wie bisher.
    const givenCart = productDetail.cart && Array.isArray(productDetail.cart.items) ? productDetail.cart : null;
    (givenCart ? Promise.resolve(givenCart) : fetch('/cart.js').then(r => r.json()))
      .then(cart => {
        currentCart = cart;
        upsellProducts = filterUpsellsNotInCart(upsellProducts, cart);
        
        if (upsellProducts.length === 0 && productDetail.upsell_products && productDetail.upsell_products.length > 0) {
          openCartDrawer();
          return;
        }
        
        // Derselbe Variant kann ZWEIMAL im Cart liegen (Einmalkauf + Abo).
        // find() traf dann evtl. die falsche Zeile -> bei Abo bevorzugt die
        // Zeile mit selling_plan_allocation waehlen.
        const matchingItems = cart.items.filter(item => item.variant_id === currentVariantId);
        const lineItem =
          (isSubscription && matchingItems.find(item => item.selling_plan_allocation)) ||
          matchingItems[0];

        // Frequenz AUTORITATIV aus dem Warenkorb, nicht aus dem DOM.
        // getSubscriptionInterval() liest im Zweifel #frequency und zeigte
        // damit eine Lieferfrequenz an, auch wenn gar kein selling_plan im
        // Warenkorb angekommen ist (z.B. weil die Abo-App kein
        // selling_plan-Feld ins Formular gibt) -- die Anzeige war kosmetisch,
        // nicht funktional. Siehe docs/61.
        // Der DOM-Wert bleibt nur Fallback, wenn der Cart nicht lesbar ist
        // (siehe .catch unten) -- dann ist eine optimistische Anzeige besser
        // als keine.
        let effectiveInterval = null;

        if (lineItem) {
          effectiveInterval = lineItem.selling_plan_allocation
            ? lineItem.selling_plan_allocation.selling_plan.name
            : null;
          currentLineKey = lineItem.key;
          
          if (!variant.featured_image && !variant.image && lineItem.image) {
            variant.featured_image = lineItem.image;
            updateNotificationImage(lineItem.image);
          }
        } else {
          // Zeile nicht gefunden -> nichts behaupten, was nicht belegt ist.
          effectiveInterval = null;
        }

        window._currentSubscriptionInterval = effectiveInterval;
        updateNotificationContent(variant, quantity, effectiveInterval);
        window._currentUpsellProducts = upsellProducts;
        updateUpsellProducts(upsellProducts);
        tlUpsellSessionStart(upsellProducts.length > 0);

        upsellDrawer.style.display = 'block';
        setTimeout(() => {
          upsellDrawer.classList.add('is-visible');
        }, 10);
      })
      .catch(err => {
        console.error('Cart fetch error:', err);
        updateNotificationContent(variant, quantity, subscriptionInterval);
        window._currentUpsellProducts = upsellProducts;
        updateUpsellProducts(upsellProducts);
        upsellDrawer.style.display = 'block';
        setTimeout(() => {
          upsellDrawer.classList.add('is-visible');
        }, 10);
      });
  };

  function filterUpsellsNotInCart(upsellProducts, cart) {
    if (!upsellProducts || upsellProducts.length === 0) return [];
    
    const cartVariantIds = cart.items.map(item => item.variant_id);
    
    return upsellProducts.filter(product => {
      const productVariantIds = [];
      
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach(v => {
          let vid = v.id || v.variant_id;
          if (typeof vid === 'string') {
            if (vid.includes(':')) vid = vid.split(':').pop();
            vid = vid.replace(/\D/g, '');
          }
          productVariantIds.push(parseInt(vid, 10));
        });
      } else {
        let vid = product.id || product.variant_id;
        if (typeof vid === 'string') {
          if (vid.includes(':')) vid = vid.split(':').pop();
          vid = vid.replace(/\D/g, '');
        }
        productVariantIds.push(parseInt(vid, 10));
      }
      
      return !productVariantIds.some(vid => cartVariantIds.includes(vid));
    });
  }

  // ============================================
  // CART DRAWER - THEME AGNOSTIC
  // ============================================

  function openCartDrawer() {
    if (themeConfig.openCartDrawerMethod === 'prestige') {
      // Prestige theme: cart-drawer is a web component with show() method
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
        return;
      }
      
      // Alternative: trigger via aria-controls button
      const cartButton = document.querySelector('[aria-controls="cart-drawer"]');
      if (cartButton) {
        cartButton.click();
        return;
      }
    }
    
    // Legacy/fallback method
    const cartTrigger = document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
    if (cartTrigger) {
      cartTrigger.click();
      return;
    }
    
    // Ultimate fallback: navigate to cart page
    window.location.href = Shopify.routes?.root ? `${Shopify.routes.root}cart` : '/cart';
  }

  function refreshCartDrawerHTML() {
    return cartQueue.add(async () => {
      if (themeConfig.openCartDrawerMethod === 'prestige') {
        // Prestige theme: dispatch cart:refresh event and let the theme handle it
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        
        // Also fetch cart data for our own updates
        const cart = await fetch('/cart.js').then(r => r.json());
        return cart;
      } else {
        // Legacy theme: manual DOM update
        const cartDrawer = document.querySelector(themeConfig.cartDrawerSelector);
        
        try {
          const response = await fetch(window.location.origin + '?view=ajax-cart&timestamp=' + Date.now());
          const html = await response.text();
          
          const temp = document.createElement('div');
          temp.innerHTML = html;
          
          if (cartDrawer) {
            const newCartItems = temp.querySelector(themeConfig.cartItemsSelector);
            const currentCartItems = cartDrawer.querySelector(themeConfig.cartItemsSelector);
            
            if (newCartItems && currentCartItems) {
              currentCartItems.innerHTML = newCartItems.innerHTML;
            }
            
            const newFooter = temp.querySelector('.Drawer__Footer__Inner');
            const currentFooter = cartDrawer.querySelector('.Drawer__Footer__Inner');
            
            if (newFooter && currentFooter) {
              const dcartElement = currentFooter.querySelector('.Drawer__Footer__Coupon-dcart');
              currentFooter.innerHTML = newFooter.innerHTML;
              
              if (dcartElement) {
                const couponContent = currentFooter.querySelector('.Drawer__Footer__Coupon-content');
                if (couponContent) {
                  couponContent.appendChild(dcartElement);
                }
              }
            }
            
            const newCartValues = temp.querySelector('.Cart__values');
            const currentCartValues = cartDrawer.querySelector('.Cart__values');
            
            if (newCartValues && currentCartValues) {
              currentCartValues.dataset.cartTotalPriceFloat = newCartValues.dataset.cartTotalPriceFloat;
            }
            
            setTimeout(() => {
              const changeEvent = new Event('change', { bubbles: true });
              cartDrawer.dispatchEvent(changeEvent);
              
              if (typeof window.unlockCheckoutButton === 'function') {
                window.unlockCheckoutButton();
              }
            }, 100);
          }
          
          // Fetch cart data
          return await fetch('/cart.js').then(r => r.json());
        } catch (error) {
          console.error('Cart refresh error:', error);
          return null;
        }
      }
    });
  }

  function updateCartCount(cartData) {
    if (!cartData || cartData.item_count === undefined) return;
    
    // Prestige theme uses <cart-count> web component that auto-updates via cart:change event
    // But we'll also try to update manually for immediate feedback
    for (const selector of themeConfig.cartCountSelectors) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el.tagName.toLowerCase() === 'cart-count') {
          // Web component - set itemCount property
          el.itemCount = cartData.item_count;
        } else {
          // Regular element - set text content
          el.textContent = cartData.item_count;
        }
      });
    }
    
    // Dispatch cart:change event for Prestige theme
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        cart: cartData
      }
    }));
  }

  // ============================================
  // NOTIFICATION CONTENT UPDATES
  // ============================================

  function updateNotificationContent(variant, quantity, subscriptionInterval) {
    updateNotificationImage(variant);

    const titleEl = upsellDrawer.querySelector('.ProductNotification__ProductTitle');
    const subscriptionInfoEl = upsellDrawer.querySelector('.ProductNotification__SubscriptionInfo');
    
    // Set product URL on title and image links
    const productHandle = variant.handle || '';
    const productUrl = productHandle ? `/products/${productHandle}` : (variant.url || '#');
    const titleLink = upsellDrawer.querySelector('.ProductNotification__TitleLink');
    const imageLink = upsellDrawer.querySelector('.ProductNotification__ImageLink');
    if (titleLink) titleLink.href = productUrl;
    if (imageLink) imageLink.href = productUrl;
    
    if (titleEl) {
      const productName = variant.product_title || variant.name || 'Product';
      const cleanedProductName = productName.split(' - ')[0].trim();
      
      let fullTitle = cleanedProductName;
      
      if (variant.title && variant.title !== 'Default Title') {
        fullTitle = `${cleanedProductName} – ${variant.title}`;
      }
      
      titleEl.textContent = fullTitle;
    }
    
    if (subscriptionInfoEl) {
      if (subscriptionInterval) {
        subscriptionInfoEl.textContent = subscriptionInterval;
        subscriptionInfoEl.style.display = 'block';
      } else {
        subscriptionInfoEl.style.display = 'none';
      }
    }

    updatePricing(variant);

    const quantityInput = upsellDrawer.querySelector('.QuantitySelector__Input');
    if (quantityInput) {
      quantityInput.value = quantity;
    }
  }

  function updatePricing(variant) {
    const priceEl = upsellDrawer.querySelector('.ProductNotification__ProductPrice');
    const comparePriceEl = upsellDrawer.querySelector('.ProductNotification__ComparePrice');
    
    if (!priceEl) return;

    let formattedPrice = formatMoney(variant.price);
    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (hasComparePrice && comparePriceEl) {
      let formattedComparePrice = formatMoney(variant.compare_at_price);

      comparePriceEl.textContent = formattedComparePrice;
      comparePriceEl.classList.add('has-compare-price');
      priceEl.classList.add('on-sale');
      priceEl.textContent = formattedPrice;
    } else {
      if (comparePriceEl) {
        comparePriceEl.textContent = '';
        comparePriceEl.classList.remove('has-compare-price');
      }
      priceEl.classList.remove('on-sale');
      priceEl.textContent = formattedPrice;
    }
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || window.themeVariables?.settings?.moneyFormat || '{{amount}} €');
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function updateNotificationImage(variantOrImageUrl) {
    const imageContainer = upsellDrawer.querySelector('.ProductNotification__Image');
    if (!imageContainer) return;

    let imageSrc = null;
    
    if (typeof variantOrImageUrl === 'string') {
      imageSrc = variantOrImageUrl;
    } else {
      const variant = variantOrImageUrl;
      
      if (variant.featured_image && variant.featured_image.src) {
        imageSrc = variant.featured_image.src;
      } else if (variant.featured_image && typeof variant.featured_image === 'string') {
        imageSrc = variant.featured_image;
      } else if (variant.image) {
        imageSrc = variant.image;
      } else if (variant.featured_media && variant.featured_media.preview_image) {
        imageSrc = variant.featured_media.preview_image.src;
      }
    }
    
    if (imageSrc) {
      if (!imageSrc.startsWith('http')) {
        imageSrc = 'https:' + imageSrc;
      }
      
      imageSrc = imageSrc.replace(/\.(jpg|jpeg|png|gif|webp)/, '_300x300.$1');
      imageContainer.src = imageSrc;
    }
  }

  // ============================================
  // UPSELL PRODUCTS
  // ============================================

  function updateUpsellProducts(upsellProducts) {
    const upsellSection = upsellDrawer.querySelector('.ProductNotification__Upsells');
    const upsellList = upsellDrawer.querySelector('.ProductNotification__UpsellList');
    
    if (!upsellSection || !upsellList) return;

    upsellList.innerHTML = '';

    if (!upsellProducts || upsellProducts.length === 0) {
      upsellSection.style.display = 'none';
      return;
    }

    upsellSection.style.display = 'block';

    upsellProducts.forEach((product, index) => {
      const upsellItem = createUpsellProductElement(product, index);
      upsellList.appendChild(upsellItem);
    });
  }

  function createUpsellProductElement(product, index) {
    const div = document.createElement('div');
    div.className = 'UpsellProduct';

    const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : product;
    const hasMultipleVariants = product.variants && product.variants.length > 1;
    
    // Get first variant ID
    let firstVariantId = firstVariant.id || firstVariant.variant_id;
    if (typeof firstVariantId === 'string') {
      if (firstVariantId.includes(':')) firstVariantId = firstVariantId.split(':').pop();
      firstVariantId = firstVariantId.replace(/\D/g, '');
    }
    firstVariantId = parseInt(firstVariantId, 10);

    let imageSrc = getProductImage(firstVariant, product);
    if (imageSrc) {
      imageSrc = imageSrc.replace(/\.(jpg|jpeg|png|gif|webp)/, '_200x200.$1');
    }

    const price = firstVariant.price || product.price;
    const comparePrice = firstVariant.compare_at_price || product.compare_at_price;
    const formattedPrice = formatMoney(price);
    const formattedComparePrice = comparePrice ? formatMoney(comparePrice) : null;

    const hasComparePrice = comparePrice && comparePrice > price;
    const priceClass = hasComparePrice ? 'UpsellProduct__Price on-sale' : 'UpsellProduct__Price';
    const comparePriceClass = hasComparePrice ? 'UpsellProduct__ComparePrice has-compare-price' : 'UpsellProduct__ComparePrice';

    // Build variant selector HTML for UpsellProduct
    let variantSelectorHTML = '';
    if (hasMultipleVariants && showVariantSelector) {
      const variantOptions = product.variants.map(v => {
        let vid = v.id || v.variant_id;
        if (typeof vid === 'string') {
          if (vid.includes(':')) vid = vid.split(':').pop();
          vid = vid.replace(/\D/g, '');
        }
        vid = parseInt(vid, 10);
        
        const variantName = v.title && v.title !== 'Default Title' ? v.title : 'Standard';
        const selected = vid === firstVariantId ? 'selected' : '';
        // Resolve variant image: try featured_image.src, then featured_image (string), then image, then fall back to product image
        const variantImage = (v.featured_image && v.featured_image.src) || (typeof v.featured_image === 'string' && v.featured_image) || (typeof v.image === 'string' && v.image) || (product.featured_image && product.featured_image.src) || (typeof product.featured_image === 'string' && product.featured_image) || '';
        return `<option value="${vid}" data-price="${v.price || 0}" data-compare-price="${v.compare_at_price || 0}" data-image="${variantImage}">${variantName}</option>`;
      }).join('');
      
      variantSelectorHTML = `
        <div class="UpsellProduct__VariantSelector">
          <select class="UpsellProduct__VariantSelect" data-action="change-upsell-product-variant">
            ${variantOptions}
          </select>
          <svg class="UpsellProduct__VariantSelectIcon" width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      `;
    }

    // Wenn der Variant-Selector aus ist: gewählte (erste) Variante als Text zwischen Titel und Preis
    // (unabhängig von der Variantenanzahl, solange die Variante einen echten Namen hat)
    let variantTextHTML = '';
    if (!showVariantSelector && product.variants && product.variants.length > 0) {
      const selName = (firstVariant.title && firstVariant.title !== 'Default Title') ? firstVariant.title : '';
      if (selName) variantTextHTML = `<div class="UpsellProduct__Variant">${selName}</div>`;
    }

    // Product title (without variant in title since we have selector)
    const productTitle = product.title ? product.title.split(' - ')[0].trim() : (product.product_title || product.name || 'Product');
    const productUrl = product.url || `/products/${product.handle || ''}`;

    div.innerHTML = `
      <div class="UpsellProduct__ImageWrapper">
        <a href="${productUrl}" class="UpsellProduct__ImageLink">
          <img class="UpsellProduct__Image" src="${imageSrc || ''}" alt="${productTitle}" />
        </a>
      </div>
      <div class="UpsellProduct__Info">
        <a href="${productUrl}" class="UpsellProduct__TitleLink">
          <h4 class="UpsellProduct__Title">${productTitle}</h4>
        </a>
        ${variantTextHTML}
        <div class="UpsellProduct__PriceContainer">
          <span class="${comparePriceClass}">${hasComparePrice ? formattedComparePrice : ''}</span>
          <span class="${priceClass}">${formattedPrice}</span>
        </div>
        <div class="UpsellProduct__VariantRow">
          ${variantSelectorHTML}
        </div>
      </div>
      <button class="UpsellProduct__AddButton" data-action="add-upsell" data-upsell-index="${index}" data-selected-variant-id="${firstVariantId}" aria-label="Add to cart">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    `;
    
    // Store product reference for variant data access
    div._productData = product;

    return div;
  }

  function getProductImage(variant, product) {
    let imageSrc = '';
    if (variant.featured_image && variant.featured_image.src) {
      imageSrc = variant.featured_image.src;
    } else if (variant.featured_image && typeof variant.featured_image === 'string') {
      imageSrc = variant.featured_image;
    } else if (variant.image) {
      imageSrc = variant.image;
    } else if (product.featured_image && product.featured_image.src) {
      imageSrc = product.featured_image.src;
    } else if (product.featured_image && typeof product.featured_image === 'string') {
      imageSrc = product.featured_image;
    } else if (product.image) {
      imageSrc = product.image;
    }

    if (imageSrc && !imageSrc.startsWith('http')) {
      imageSrc = 'https:' + imageSrc;
    }
    
    return imageSrc;
  }

  // ============================================
  // ADD TO CART FUNCTIONS
  // ============================================

  function addUpsellToCart(product, buttonElement) {
    // Get selected variant ID from button or dropdown
    const selectedVariantId = buttonElement.dataset.selectedVariantId;
    let variant;
    
    if (selectedVariantId && product.variants && product.variants.length > 0) {
      // Find the selected variant
      variant = product.variants.find(v => {
        let vid = v.id || v.variant_id;
        if (typeof vid === 'string') {
          if (vid.includes(':')) vid = vid.split(':').pop();
          vid = vid.replace(/\D/g, '');
        }
        return parseInt(vid, 10) === parseInt(selectedVariantId, 10);
      });
    }
    
    // Fallback to first variant if not found
    if (!variant) {
      variant = product.variants && product.variants.length > 0 ? product.variants[0] : product;
    }
    
    addVariantToCart(variant, product, buttonElement);
  }

  function addVariantToCart(variant, product, buttonElement) {
    let variantId = variant.id || variant.variant_id;
    
    if (typeof variantId === 'string') {
      if (variantId.includes(':')) variantId = variantId.split(':').pop();
      variantId = variantId.replace(/\D/g, '');
    }
    
    variantId = parseInt(variantId, 10);
    
    if (!variantId || isNaN(variantId)) {
      alert('Invalid product variant');
      return;
    }

    buttonElement.classList.add('is-loading');
    buttonElement.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-dasharray="40" stroke-dashoffset="10" opacity="0.5"><animateTransform attributeName="transform" type="rotate" from="0 10 10" to="360 10 10" dur="1s" repeatCount="indefinite"/></circle></svg>';

    const formData = {
      items: [{
        id: variantId,
        quantity: 1
      }]
    };

    // Use queued request to prevent race conditions
    queuedCartFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(addResponse => {
      // addResponse contains the added item(s), now refresh HTML which also gives us cart data
      tlUpsellAccepted();
      if (window.TL && window.TL.upsellModal && window.TL.upsellModal.recordUpsell) {
        window.TL.upsellModal.recordUpsell('drawer', product.title || product.product_title || variant.title, variant.price);
      }
      return refreshCartDrawerHTML().then(cart => {
        if (cart) {
          updateCartCount(cart);
          
          const lineItem = cart.items.find(item => item.variant_id === variantId);
          if (lineItem) {
            const productData = {
              ...product,
              ...variant,
              product_title: product.product_title || product.name,
              title: variant.title
            };
            addUpsellProductCard(productData, lineItem, product);
          }
        }

        buttonElement.classList.remove('is-loading');
        buttonElement.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10L8 14L16 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        buttonElement.style.background = '#86BC99';
        buttonElement.disabled = true;
        
        const upsellItem = buttonElement.closest('.UpsellProduct');
        if (upsellItem) {
          upsellItem.style.display = 'none';
          upsellItem.style.pointerEvents = 'none';
          upsellItem.dataset.variantId = variantId;
        }
      });
    })
    .catch(error => {
      buttonElement.classList.remove('is-loading');
      buttonElement.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      console.error('Add to cart error:', error);
      alert('Unable to add product: ' + error.message);
    });
  }

  // ============================================
  // ADDED UPSELL CARD (after adding to cart)
  // ============================================

  function addUpsellProductCard(product, lineItem, originalProduct) {
    const addedSection = upsellDrawer.querySelector('.ProductNotification__AddedUpsells');
    if (!addedSection) return;

    addedSection.style.display = 'flex';

    const cardElement = createAddedUpsellCard(product, lineItem, originalProduct);
    addedSection.appendChild(cardElement);
  }

  function createAddedUpsellCard(product, lineItem, originalProduct) {
    const div = document.createElement('div');
    div.className = 'AddedUpsellCard';
    div.dataset.lineKey = lineItem.key;
    div.dataset.variantId = product.id || product.variant_id || lineItem.variant_id;
    
    // Store original product data for variant switching
    if (originalProduct) {
      div.dataset.productHandle = originalProduct.handle || '';
      div.dataset.productId = originalProduct.id || '';
    }

    let imageSrc = lineItem.image || '';
    if (!imageSrc) {
      imageSrc = getProductImage(product, originalProduct || product);
    }

    if (imageSrc && !imageSrc.startsWith('http')) {
      imageSrc = 'https:' + imageSrc;
    }

    if (imageSrc) {
      imageSrc = imageSrc.replace(/\.(jpg|jpeg|png|gif|webp)/, '_300x300.$1');
    }

    const formattedPrice = formatMoney(product.price || lineItem.price);
    const formattedComparePrice = product.compare_at_price ? formatMoney(product.compare_at_price) : null;

    const hasComparePrice = product.compare_at_price && product.compare_at_price > product.price;
    const priceClass = hasComparePrice ? 'AddedUpsellCard__Price on-sale' : 'AddedUpsellCard__Price';
    const comparePriceClass = hasComparePrice ? 'AddedUpsellCard__ComparePrice has-compare-price' : 'AddedUpsellCard__ComparePrice';

    const currentVariantTitle = product.title && product.title !== 'Default Title' ? product.title : lineItem.variant_title;
    const productName = product.product_title || product.name || lineItem.product_title || 'Product';
    const cleanedProductName = productName.split(' - ')[0].trim();
    
    // Build full title with variant (like ProductNotification__ProductTitle)
    let fullTitle = cleanedProductName;
    if (currentVariantTitle && currentVariantTitle !== 'Default Title') {
      fullTitle = `${cleanedProductName} – ${currentVariantTitle}`;
    }

    // Check if product has multiple variants for selector
    const hasMultipleVariants = originalProduct && originalProduct.variants && originalProduct.variants.length > 1;
    const currentVariantId = product.id || product.variant_id || lineItem.variant_id;
    
    // Build variant selector HTML
    let variantSelectorHTML = '';
    if (hasMultipleVariants && showVariantSelector) {
      const variantOptions = originalProduct.variants.map(v => {
        let vid = v.id || v.variant_id;
        if (typeof vid === 'string') {
          if (vid.includes(':')) vid = vid.split(':').pop();
          vid = vid.replace(/\D/g, '');
        }
        vid = parseInt(vid, 10);
        
        const variantName = v.title && v.title !== 'Default Title' ? v.title : 'Standard';
        const selected = vid === parseInt(currentVariantId) ? 'selected' : '';
        return `<option value="${vid}" ${selected}>${variantName}</option>`;
      }).join('');
      
      variantSelectorHTML = `
        <div class="AddedUpsellCard__VariantSelector">
          <select class="AddedUpsellCard__VariantSelect" data-action="change-upsell-variant">
            ${variantOptions}
          </select>
          <svg class="AddedUpsellCard__VariantSelectIcon" width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      `;
    }

    const productUrl = (originalProduct && originalProduct.url) || `/products/${(originalProduct && originalProduct.handle) || ''}`;

    div.innerHTML = `
      <div class="AddedUpsellCard__ImageWrapper">
        <a href="${productUrl}" class="AddedUpsellCard__ImageLink">
          <img class="AddedUpsellCard__Image" src="${imageSrc}" alt="${productName}" />
        </a>
      </div>
      <div class="AddedUpsellCard__Details">
        <a href="${productUrl}" class="AddedUpsellCard__TitleLink">
          <h4 class="AddedUpsellCard__Title">${fullTitle}</h4>
        </a>
        ${variantSelectorHTML}
        <div class="AddedUpsellCard__PriceContainer">
          ${hasComparePrice ? `<span class="${comparePriceClass}">${formattedComparePrice}</span>` : ''}
          <span class="${priceClass}">${formattedPrice}</span>
        </div>
        <div class="AddedUpsellCard__QuantityWrapper">
          <div class="QuantitySelector">
            <button class="QuantitySelector__Button QuantitySelector__Button--Minus" 
                    data-action="decrease-added-upsell-qty" 
                    aria-label="Decrease quantity">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                <path d="M0 1H10" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
            <input type="number" 
                   class="QuantitySelector__Input" 
                   value="1" 
                   min="0" 
                   aria-label="Quantity" />
            <button class="QuantitySelector__Button QuantitySelector__Button--Plus" 
                    data-action="increase-added-upsell-qty" 
                    aria-label="Increase quantity">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M5 0V10M0 5H10" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
    
    // Store original product reference for variant switching
    if (originalProduct) {
      div._originalProduct = originalProduct;
    }

    return div;
  }

  // ============================================
  // UPSELL PRODUCT VARIANT SWITCHING (before add to cart)
  // ============================================

  function updateUpsellProductVariant(upsellItem, selectElement) {
    const product = upsellItem._productData;
    if (!product || !product.variants) return;
    
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const newVariantId = selectElement.value;
    const newPrice = parseInt(selectedOption.dataset.price) || 0;
    const newComparePrice = parseInt(selectedOption.dataset.comparePrice) || 0;
    let newImage = selectedOption.dataset.image || '';
    
    // Update the add button with new variant ID
    const addButton = upsellItem.querySelector('.UpsellProduct__AddButton');
    if (addButton) {
      addButton.dataset.selectedVariantId = newVariantId;
    }
    
    // Update price display
    const priceEl = upsellItem.querySelector('.UpsellProduct__Price');
    const comparePriceEl = upsellItem.querySelector('.UpsellProduct__ComparePrice');
    const priceContainer = upsellItem.querySelector('.UpsellProduct__PriceContainer');
    
    if (priceEl) {
      const formattedPrice = formatMoney(newPrice);
      priceEl.textContent = formattedPrice;
      
      const hasComparePrice = newComparePrice && newComparePrice > newPrice;
      
      if (hasComparePrice) {
        priceEl.classList.add('on-sale');
        const formattedComparePrice = formatMoney(newComparePrice);
        
        if (comparePriceEl) {
          comparePriceEl.textContent = formattedComparePrice;
          comparePriceEl.classList.add('has-compare-price');
        } else if (priceContainer) {
          // Create compare price element if it doesn't exist
          const newComparePriceEl = document.createElement('span');
          newComparePriceEl.className = 'UpsellProduct__ComparePrice has-compare-price';
          newComparePriceEl.textContent = formattedComparePrice;
          priceContainer.insertBefore(newComparePriceEl, priceEl);
        }
      } else {
        priceEl.classList.remove('on-sale');
        if (comparePriceEl) {
          comparePriceEl.textContent = '';
          comparePriceEl.classList.remove('has-compare-price');
        }
      }
    }
    
    // Update image if variant has one
    if (newImage && newImage !== 'null' && !newImage.includes('[object')) {
      if (!newImage.startsWith('http')) {
        newImage = 'https:' + newImage;
      }
      newImage = newImage.replace(/\.(jpg|jpeg|png|gif|webp)/, '_200x200.$1');
      
      const imageEl = upsellItem.querySelector('.UpsellProduct__Image');
      if (imageEl) {
        imageEl.src = newImage;
      }
    }
  }

  // ============================================
  // VARIANT SWITCHING FOR ADDED UPSELLS
  // ============================================

  function changeUpsellVariant(cardElement, newVariantId) {
    const originalProduct = cardElement._originalProduct;
    if (!originalProduct || !originalProduct.variants) return;
    
    const currentLineKey = cardElement.dataset.lineKey;
    const currentQuantity = parseInt(cardElement.querySelector('.QuantitySelector__Input').value) || 1;
    
    // Find the new variant
    const newVariant = originalProduct.variants.find(v => {
      let vid = v.id || v.variant_id;
      if (typeof vid === 'string') {
        if (vid.includes(':')) vid = vid.split(':').pop();
        vid = vid.replace(/\D/g, '');
      }
      return parseInt(vid, 10) === parseInt(newVariantId, 10);
    });
    
    if (!newVariant) return;
    
    // Show loading state
    const select = cardElement.querySelector('.AddedUpsellCard__VariantSelect');
    if (select) select.disabled = true;
    
    // Use queued requests to prevent race conditions
    queuedCartFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: currentLineKey, quantity: 0 })
    })
    .then(() => {
      return queuedCartFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(newVariantId, 10), quantity: currentQuantity }] })
      });
    })
    .then(() => refreshCartDrawerHTML())
    .then(cart => {
      if (cart) {
        updateCartCount(cart);
        
        // Find the new line item
        const newLineItem = cart.items.find(item => item.variant_id === parseInt(newVariantId, 10));
        
        if (newLineItem) {
          // Update card data
          cardElement.dataset.lineKey = newLineItem.key;
          cardElement.dataset.variantId = newVariantId;
        }
      }
      
      // Update title with new variant
      const productName = originalProduct.title || originalProduct.name || 'Product';
      const cleanedProductName = productName.split(' - ')[0].trim();
      const newVariantTitle = newVariant.title && newVariant.title !== 'Default Title' ? newVariant.title : '';
      
      let fullTitle = cleanedProductName;
      if (newVariantTitle) {
        fullTitle = `${cleanedProductName} – ${newVariantTitle}`;
      }
      
      const titleEl = cardElement.querySelector('.AddedUpsellCard__Title');
      if (titleEl) titleEl.textContent = fullTitle;
      
      // Update price with proper sale/compare price handling
      const priceContainer = cardElement.querySelector('.AddedUpsellCard__PriceContainer');
      
      const newPrice = newVariant.price;
      const newComparePrice = newVariant.compare_at_price;
      const hasComparePrice = newComparePrice && newComparePrice > newPrice;
      
      // Format prices
      const formattedPrice = formatMoney(newPrice);
      const formattedComparePrice = hasComparePrice ? formatMoney(newComparePrice) : '';
      
      // Rebuild price container content for clean state
      if (priceContainer) {
        if (hasComparePrice) {
          priceContainer.innerHTML = `
            <span class="AddedUpsellCard__ComparePrice has-compare-price">${formattedComparePrice}</span>
            <span class="AddedUpsellCard__Price on-sale">${formattedPrice}</span>
          `;
        } else {
          priceContainer.innerHTML = `
            <span class="AddedUpsellCard__Price">${formattedPrice}</span>
          `;
        }
      }
      
      // Update image if variant has one
      if (newVariant.featured_image || newVariant.image) {
        let newImageSrc = newVariant.featured_image?.src || newVariant.featured_image || newVariant.image;
        if (newImageSrc && !newImageSrc.startsWith('http')) {
          newImageSrc = 'https:' + newImageSrc;
        }
        if (newImageSrc) {
          newImageSrc = newImageSrc.replace(/\.(jpg|jpeg|png|gif|webp)/, '_300x300.$1');
          const imageEl = cardElement.querySelector('.AddedUpsellCard__Image');
          if (imageEl) imageEl.src = newImageSrc;
        }
      }
      
      if (select) select.disabled = false;
    })
    .catch(error => {
      console.error('Variant change error:', error);
      if (select) select.disabled = false;
      // Revert select to previous value
      const oldVariantId = cardElement.dataset.variantId;
      if (select && oldVariantId) select.value = oldVariantId;
    });
  }

  // ============================================
  // QUANTITY UPDATES
  // ============================================

  function updateAddedUpsellQuantity(cardElement, newQuantity) {
    const lineKey = cardElement.dataset.lineKey;
    if (!lineKey) return;

    queuedCartFetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: newQuantity
      })
    })
    .then(() => refreshCartDrawerHTML())
    .then(cart => {
      if (cart) {
        updateCartCount(cart);
      }
      
      if (newQuantity === 0) {
        const variantId = parseInt(cardElement.dataset.variantId);
        const upsellItems = upsellDrawer.querySelectorAll('.UpsellProduct');
        upsellItems.forEach(item => {
          if (parseInt(item.dataset.variantId) === variantId) {
            item.style.display = 'flex';
            item.style.pointerEvents = 'auto';
            const button = item.querySelector('.UpsellProduct__AddButton');
            if (button) {
              button.disabled = false;
              button.style.background = '';
              button.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
            }
          }
        });
        
        cardElement.remove();
        
        const addedSection = upsellDrawer.querySelector('.ProductNotification__AddedUpsells');
        if (addedSection && addedSection.children.length === 0) {
          addedSection.style.display = 'none';
        }
      } else if (cart) {
        const variantId = parseInt(cardElement.dataset.variantId);
        const lineItem = cart.items.find(item => item.variant_id === variantId);
        if (lineItem) {
          cardElement.dataset.lineKey = lineItem.key;
        }
      }
    })
    .catch(error => {
      console.error('Update quantity error:', error);
      const input = cardElement.querySelector('.QuantitySelector__Input');
      if (input) {
        input.value = parseInt(input.value) === newQuantity + 1 ? newQuantity : newQuantity + 1;
      }
    });
  }

  function closeNotification() {
    tlUpsellSessionEnd();
    upsellDrawer.classList.remove('is-visible');
    setTimeout(() => {
      upsellDrawer.style.display = 'none';
      
      const addedSection = upsellDrawer.querySelector('.ProductNotification__AddedUpsells');
      if (addedSection) {
        addedSection.innerHTML = '';
        addedSection.style.display = 'none';
      }
      
      const upsellItems = upsellDrawer.querySelectorAll('.UpsellProduct');
      upsellItems.forEach(item => {
        item.style.opacity = '1';
        item.style.pointerEvents = 'auto';
      });
    }, 300);
  }

  function updateCartQuantity(newQuantity) {
    if (isUpdating || !currentLineKey) return;

    isUpdating = true;

    queuedCartFetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: currentLineKey,
        quantity: newQuantity
      })
    })
    .then(() => refreshCartDrawerHTML())
    .then(cart => {
      isUpdating = false;
      currentQuantity = newQuantity;
      
      if (cart) {
        updateCartCount(cart);
      }
      
      if (newQuantity === 0) {
        closeNotification();
      }
    })
    .catch(error => {
      isUpdating = false;
      console.error('Update cart quantity error:', error);
      
      const input = upsellDrawer.querySelector('.QuantitySelector__Input');
      if (input) {
        input.value = currentQuantity;
      }
    });
  }

  // ============================================
  // TL UPSELL-MODAL (A/B, docs/69): Events der Kontrollgruppe + API fürs Modal
  // ============================================
  // Gleiche Events wie im Modal, damit beide Arme vergleichbar sind. Gefeuert wird nur
  // auf Testprodukten: dort ist window.TL.upsellModal geladen (tl-upsell-modal.js).
  let tlUpsellSession = false;
  let tlUpsellAdded = false;

  function tlTrack(name) {
    if (window.TL && window.TL.upsellModal) window.TL.upsellModal.track(name);
  }

  function tlUpsellSessionStart(hasVisibleUpsells) {
    tlUpsellSession = hasVisibleUpsells;
    tlUpsellAdded = false;
    if (hasVisibleUpsells) tlTrack('tl-upsell-shown');
  }

  function tlUpsellAccepted() {
    if (!tlUpsellSession || tlUpsellAdded) return;
    tlUpsellAdded = true;
    tlTrack('tl-upsell-accepted');
  }

  function tlUpsellSessionEnd() {
    if (tlUpsellSession && !tlUpsellAdded) tlTrack('tl-upsell-declined');
    tlUpsellSession = false;
  }

  window._tlUpsellDrawerApi = {
    openCartDrawer: function() {
      _blockCartDrawer = false;
      openCartDrawer();
    },
    refreshCart: refreshCartDrawerHTML,
    updateCartCount: updateCartCount,
    cartFetch: queuedCartFetch
  };

  // ============================================
  // EVENT LISTENERS
  // ============================================
  
  if (upsellDrawer) {
    const closeBtn = upsellDrawer.querySelector('[data-action="close-notification"]');
    if (closeBtn) closeBtn.addEventListener('click', closeNotification);

    const overlay = upsellDrawer.querySelector('.ProductNotification__Overlay');
    if (overlay) overlay.addEventListener('click', closeNotification);

    const continueBtn = upsellDrawer.querySelector('[data-action="continue-shopping"]');
    if (continueBtn) continueBtn.addEventListener('click', closeNotification);

    const viewCartBtn = upsellDrawer.querySelector('[data-action="view-cart"]');
    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', function() {
        closeNotification();
        // Wait for our drawer to fully close (300ms transition + buffer)
        // before opening cart drawer to avoid z-index/overlay conflicts
        setTimeout(function() {
          // Ensure block flag is cleared - we WANT to open cart drawer now
          _blockCartDrawer = false;
          openCartDrawer();
        }, 350);
      });
    }

    const decreaseBtn = upsellDrawer.querySelector('[data-action="decrease-notification-qty"]');
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', function() {
        const input = upsellDrawer.querySelector('.QuantitySelector__Input');
        const qty = parseInt(input.value);
        const newQty = qty - 1;
        
        if (newQty >= 0) {
          currentQuantity = newQty;
          input.value = newQty;
          updateCartQuantity(newQty);
        }
      });
    }

    const increaseBtn = upsellDrawer.querySelector('[data-action="increase-notification-qty"]');
    if (increaseBtn) {
      increaseBtn.addEventListener('click', function() {
        const input = upsellDrawer.querySelector('.QuantitySelector__Input');
        const qty = parseInt(input.value);
        const newQty = qty + 1;
        
        currentQuantity = newQty;
        input.value = newQty;
        updateCartQuantity(newQty);
      });
    }

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && upsellDrawer.classList.contains('is-visible')) {
        closeNotification();
      }
    });

    upsellDrawer.addEventListener('click', function(e) {
      const addButton = e.target.closest('[data-action="add-upsell"]');
      if (addButton) {
        const index = parseInt(addButton.dataset.upsellIndex);
        if (window._currentUpsellProducts && window._currentUpsellProducts[index]) {
          addUpsellToCart(window._currentUpsellProducts[index], addButton);
        }
        return;
      }

      const decreaseAddedBtn = e.target.closest('[data-action="decrease-added-upsell-qty"]');
      if (decreaseAddedBtn) {
        const card = decreaseAddedBtn.closest('.AddedUpsellCard');
        const input = card.querySelector('.QuantitySelector__Input');
        const qty = parseInt(input.value);
        const newQty = qty - 1;
        
        if (newQty >= 0) {
          input.value = newQty;
          updateAddedUpsellQuantity(card, newQty);
        }
        return;
      }

      const increaseAddedBtn = e.target.closest('[data-action="increase-added-upsell-qty"]');
      if (increaseAddedBtn) {
        const card = increaseAddedBtn.closest('.AddedUpsellCard');
        const input = card.querySelector('.QuantitySelector__Input');
        const qty = parseInt(input.value);
        const newQty = qty + 1;
        
        input.value = newQty;
        updateAddedUpsellQuantity(card, newQty);
        return;
      }
    });

    // Variant selector change event
    upsellDrawer.addEventListener('change', function(e) {
      // Handle AddedUpsellCard variant change
      const variantSelect = e.target.closest('[data-action="change-upsell-variant"]');
      if (variantSelect) {
        const card = variantSelect.closest('.AddedUpsellCard');
        const newVariantId = variantSelect.value;
        if (card && newVariantId) {
          changeUpsellVariant(card, newVariantId);
        }
        return;
      }
      
      // Handle UpsellProduct variant change (before adding to cart)
      const upsellVariantSelect = e.target.closest('[data-action="change-upsell-product-variant"]');
      if (upsellVariantSelect) {
        const upsellItem = upsellVariantSelect.closest('.UpsellProduct');
        if (upsellItem) {
          updateUpsellProductVariant(upsellItem, upsellVariantSelect);
        }
        return;
      }

      // Handle manual quantity input on main product
      if (e.target.closest('.ProductNotification__QuantityWrapper .QuantitySelector__Input')) {
        const input = e.target;
        const newQty = Math.max(0, parseInt(input.value) || 0);
        input.value = newQty;
        currentQuantity = newQty;
        updateCartQuantity(newQty);
        return;
      }

      // Handle manual quantity input on added upsell cards
      if (e.target.closest('.AddedUpsellCard .QuantitySelector__Input')) {
        const input = e.target;
        const card = input.closest('.AddedUpsellCard');
        if (card) {
          const newQty = Math.max(0, parseInt(input.value) || 0);
          input.value = newQty;
          changeAddedUpsellQuantity(card, newQty);
        }
      }
    });
  }

  // ============================================
  // PRESTIGE THEME EVENT INTEGRATION
  // ============================================
  
  // We use capturing phase to intercept variant:add before Prestige's listeners.
  // HOWEVER: Prestige opens cart-drawer via a DIRECT call chain from ProductForm.onSubmit,
  // NOT from this event. So stopImmediatePropagation alone is not enough.
  // We ALSO set _blockCartDrawer = true so our monkey-patched show() blocks it.
  document.addEventListener('variant:add', function(e) {
    if (e.detail && typeof window._showProductNotification === 'function') {
      const hasUpsells = window._pendingUpsellProducts && window._pendingUpsellProducts.length > 0;

      // TL Upsell-Modal (A/B, docs/69): auf Testprodukten im Varianten-Arm ersetzt das
      // Modal den Drawer. Synchron entscheiden, ob es in Frage kommt -- die Blockade muss
      // stehen, bevor Prestige per cart:change den Cart-Drawer öffnet.
      const modalApi = window.TL && window.TL.upsellModal;
      const addedItems = e.detail.items || [];
      const modalCandidate = !!(modalApi && addedItems[0] && modalApi.isEligible(addedItems[0].product_id));

      if (hasUpsells || modalCandidate) {
        // Set the block flag BEFORE Prestige's onCartChange_fn calls show()
        _blockCartDrawer = true;
        e.stopImmediatePropagation();
        console.log('[Upsell Drawer] Intercepted variant:add — set cart drawer block');

        const items = e.detail.items || [];
        if (items.length > 0) {
          const item = items[0];
          // Prestige holt /cart.js direkt nach dem Add und liefert ihn in e.detail.cart mit
          // (theme.js, ProductForm) -> nicht noch einmal laden (Beschleunigung 20.10., docs/69).
          const detailCart = e.detail.cart && Array.isArray(e.detail.cart.items) ? e.detail.cart : null;
          (detailCart ? Promise.resolve(detailCart) : fetch('/cart.js').then(r => r.json()))
            .then(cart => {
              if (modalCandidate && modalApi.shouldShowForCart(cart)) {
                modalApi.show();
                return;
              }
              // Modal greift nicht (Upsell schon im Warenkorb) und keine Standard-Upsells
              // -> genau das, was ohne Abfangen passiert wäre: Cart-Drawer.
              if (!hasUpsells) {
                _blockCartDrawer = false;
                openCartDrawer();
                return;
              }

              const lineItem = cart.items.find(li => li.key === item.key || li.variant_id === item.variant_id);
              if (lineItem) {
                const handle = lineItem.handle || lineItem.url?.split('/products/')[1]?.split('?')[0] || '';
                
                // Cart API liefert keinen compare_at_price. Auf der Produktseite steht er schon in
                // window._tlMainCompareAt (upsell-products-loader.liquid); nur wenn die Variante
                // dort fehlt (anderes Produkt), wie bisher /products/<handle>.js nachladen.
                const pageCompareAt = window._tlMainCompareAt || {};
                const hasPageCompareAt = Object.prototype.hasOwnProperty.call(pageCompareAt, String(lineItem.variant_id));
                const productFetch = hasPageCompareAt
                  ? Promise.resolve({ variants: [{ id: lineItem.variant_id, compare_at_price: pageCompareAt[String(lineItem.variant_id)] }] })
                  : handle
                    ? fetch('/products/' + handle + '.js').then(r => r.json()).catch(() => null)
                    : Promise.resolve(null);

                productFetch.then(productData => {
                  let compareAtPrice = null;

                  if (productData && productData.variants) {
                    const matchedVariant = productData.variants.find(v => v.id === lineItem.variant_id);
                    if (matchedVariant && matchedVariant.compare_at_price && matchedVariant.compare_at_price > lineItem.price) {
                      compareAtPrice = matchedVariant.compare_at_price;
                    }
                  }

                  const productDetail = {
                    variant: {
                      id: lineItem.variant_id,
                      variant_id: lineItem.variant_id,
                      product_title: lineItem.product_title,
                      title: lineItem.variant_title,
                      price: lineItem.price,
                      compare_at_price: compareAtPrice,
                      image: lineItem.image,
                      handle: handle
                    },
                    quantity: lineItem.quantity,
                    upsell_products: window._pendingUpsellProducts || [],
                    cart: cart
                  };
                  window._showProductNotification(productDetail);
                });
              }
            })
            .catch(err => console.error('[Upsell Drawer] Cart fetch error:', err));
        }
      }
    }
  }, true);

  // Legacy event listener for compatibility
  document.addEventListener('product:added', function(e) {
    if (e.detail && typeof window._showProductNotification === 'function') {
      const hasUpsells = window._pendingUpsellProducts && window._pendingUpsellProducts.length > 0;
      if (hasUpsells) {
        e.stopImmediatePropagation();
        window._showProductNotification(e.detail);
      }
    }
  }, true);

  console.log('[Upsell Drawer] Initialized with theme config:', themeConfig.openCartDrawerMethod);
})();